'use client';

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, CircularProgress, Container, Typography, Stack } from '@mui/material';

import { CreatorWorkspace } from './components/CreatorWorkspace';
import { CashOutModal } from '../CashOutModal';
import { InfluencerOnboardingModal } from './components/InfluencerOnboardingModal';
import { BuzzBridgeCreatorOnboardingScreen } from './components/BuzzBridgeCreatorOnboardingScreen';
import { Pagination } from './components/Pagination';
import { RequestsList } from './components/RequestsList';
import { InvitationsList } from './components/InvitationsList';
import { EmptyState } from './components/EmptyState';
import { EditProfileModal } from './components/EditProfileModal';
import { WorkManagementJobList } from './components/WorkManagementJobList';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { useDispatchPromise } from '../../utils/AppUtils';
import { getBuzzBridgeProfile, getCommissionWallet, updateBuzzBridgeProfile } from '../../redux/buzzbridge/actions';
import { useSelector } from 'react-redux';
import { buzzProfileWithLoadingSelector } from '../../selectors/selectors';
import { commissionWalletSelector, noInfluencerProfileSelector } from '../../redux/buzzbridge/selectors';
import { buildCommissionEnrollmentRows } from '../../utils/commissionWallet';
import { acceptCampaignInviteUrl, declineCampaignInviteUrl } from '../../configurations/api/url';
import { IInfluencer, IPriceCard } from '../../redux/buzzbridge/types';
import type { RateCardPlatformId } from './utils/rateCardPlatformConfig';
import { mapActiveJobsToListItems } from './utils/mapBuzzBridgeJob';
import { mapRawToBuzzBridgeRequest, type BuzzBridgeRequestRow } from './utils/mapBuzzBridgeRequest';
import { buildJobsTabRows, filterRequestsTabRows } from './utils/partitionBuzzBridgeParticipations';

const JOBS_PAGE_SIZE = 5;

export const BuzzBridgeJobDashboard = () => {
    const { themeStyle } = useThemeContext();
    const [listPage, setListPage] = useState(1);
    const [activeTab, setActiveTab] = useState('jobs');
    const [tabLoading, setTabLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const [cashOutOpen, setCashOutOpen] = useState(false);
    const [settingsTab, setSettingsTab] = useState(0);
    const [workspaceView, setWorkspaceView] = useState<'campaigns' | 'performance'>('campaigns');
    const commissionWallet = useSelector(commissionWalletSelector);
    const { data, gettingBuzzProfile } = useSelector(buzzProfileWithLoadingSelector);
    const token = useSelector((s: any) => s.authentication.token as string);

    const noInfluencerProfile = useSelector(noInfluencerProfileSelector);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();

    // Load initial profile data (header and stats)
    const handleGetInitialProfile = useCallback(async () => {
        await dispatchPromise(getBuzzBridgeProfile({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load tab-specific data
    const handleGetTabData = useCallback(async (tab: string, page: number = 1) => {
        setTabLoading(true);
        const groupType = tab === 'jobs' ? 'active_jobs' : tab === 'requests' ? 'requests' : 'invitations';
        try {
            await dispatchPromise(
                getBuzzBridgeProfile({ group_type: groupType as any, page, limit: 10, skipRefresh: true }),
            );
        } catch {
            /** 404 / errors handled in saga; keep UI stable */
        }
        setTabLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        void handleGetInitialProfile().catch(() => {
            /** Errors handled in saga + Redux; avoid unhandled rejection surfacing as overlay */
        });
    }, [handleGetInitialProfile]);

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setListPage(1);
        handleGetTabData(tab, 1);
    };

    const handlePageChange = (page: number) => {
        setListPage(page);
        if (activeTab === 'jobs') return;
        handleGetTabData(activeTab, page);
    };

    const handleOpenJob = (campaignId: string, participantId?: string) => {
        const pid = participantId?.trim();
        if (pid && campaignId) {
            let route = replaceRouteParam(ROUTES.BUZZ_BRIDGE_CAMPAIGN_WORKSPACE, 'campaignId', campaignId);
            route = replaceRouteParam(route, 'participantId', pid);
            dispatch(navigate({ routes: route, skipAuthentication: false }));
            return;
        }
        const route = replaceRouteParam(ROUTES.BUZZ_BRIDGE_DETAILS, 'campaignId', campaignId);
        dispatch(navigate({ routes: route, skipAuthentication: false }));
    };

    const handleCashOutInfo = useCallback(() => {
        setCashOutOpen(true);
    }, []);

    const handleAcceptInvitation = useCallback(
        async (participantId: string) => {
            await axios.patch(
                acceptCampaignInviteUrl(participantId),
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            handleGetTabData('invitations');
        },
        [token, handleGetTabData],
    );

    const handleRejectInvitation = useCallback(
        async (participantId: string) => {
            await axios.patch(
                declineCampaignInviteUrl(participantId),
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            handleGetTabData('invitations');
        },
        [token, handleGetTabData],
    );

    const handleEditProfile = (tab = 0) => {
        setSettingsTab(tab);
        setEditModalOpen(true);
    };

    useEffect(() => {
        dispatch(getCommissionWallet());
    }, [dispatch]);

    const handleSaveProfile = (updatedData: Partial<IInfluencer>) => {
        if (data?.data.influencer._id)
            dispatch(
                updateBuzzBridgeProfile({
                    documentId: data?.data.influencer._id,
                    payload: updatedData,
                }),
            );
        setEditModalOpen(false);
    };

    const handleSaveRateCard = useCallback(
        (platform: RateCardPlatformId, pricing: Partial<IPriceCard>) => {
            const inf = data?.data?.influencer;
            if (!inf?._id) return;
            const current = inf[platform];
            dispatch(
                updateBuzzBridgeProfile({
                    documentId: inf._id,
                    payload: {
                        [platform]: {
                            ...current,
                            pricing: {
                                ...current?.pricing,
                                ...pricing,
                            },
                        },
                    },
                }),
            );
        },
        [data?.data?.influencer, dispatch],
    );

    const tabOptions = [
        { label: 'Your Jobs', value: 'jobs' },
        { label: 'Requests', value: 'requests' },
        { label: 'Invitations', value: 'invitations' },
        { label: 'Completed', value: 'completed' },
    ];

    // Get current data based on active tab
    const getCurrentData = () => {
        if (!data?.data?.influencer) return [];
        const inf = data.data.influencer;

        switch (activeTab) {
            case 'jobs':
                return buildJobsTabRows(inf.active_jobs, inf.requests);
            case 'requests':
                return filterRequestsTabRows(inf.requests);
            case 'invitations':
                return inf.invitations || [];
            case 'completed':
                return inf.completed_jobs || [];
            default:
                return [];
        }
    };

    // Get pagination info based on active tab
    const getPaginationInfo = () => {
        if (!data) return { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false };

        switch (activeTab) {
            case 'jobs': {
                const jobCount = buildJobsTabRows(
                    data.data?.influencer?.active_jobs,
                    data.data?.influencer?.requests,
                ).length;
                const totalPages = Math.max(1, Math.ceil(jobCount / JOBS_PAGE_SIZE));
                const page = Math.min(listPage, totalPages);
                return {
                    currentPage: page,
                    totalPages,
                    hasNext: page < totalPages,
                    hasPrev: page > 1,
                };
            }
            case 'requests':
                return {
                    currentPage: data.currentPage,
                    totalPages: data.totalPages?.requests || 1,
                    hasNext: data.hasNextPage?.requests || false,
                    hasPrev: data.hasPreviousPage?.requests || false,
                };
            case 'invitations':
                return {
                    currentPage: data.currentPage,
                    totalPages: data.totalPages?.invitations || 1,
                    hasNext: data.hasNextPage?.invitations || false,
                    hasPrev: data.hasPreviousPage?.invitations || false,
                };
            case 'completed':
                return {
                    currentPage: data.currentPage,
                    totalPages: (data.totalPages as any)?.completed_jobs || 1,
                    hasNext: (data.hasNextPage as any)?.completed_jobs || false,
                    hasPrev: (data.hasPreviousPage as any)?.completed_jobs || false,
                };
            default:
                return { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false };
        }
    };

    const tabRows = getCurrentData();
    const paginationInfo = getPaginationInfo();
    const currentData =
        activeTab === 'jobs'
            ? tabRows.slice(
                  (paginationInfo.currentPage - 1) * JOBS_PAGE_SIZE,
                  paginationInfo.currentPage * JOBS_PAGE_SIZE,
              )
            : tabRows;

    // Render tab content with loading state
    const renderTabContent = () => {
        if (tabLoading) {
            return (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: theme[themeStyle].tag_white }}>
                        Loading {activeTab}...
                    </Typography>
                </Box>
            );
        }

        if (currentData.length === 0) {
            const emptyStateConfig = {
                jobs: {
                    title: 'No Active Jobs',
                    message: "You don't have any active jobs at the moment. Check back later for new opportunities!",
                },
                requests: {
                    title: 'No Requests',
                    message:
                        "You haven't made any requests yet. Start applying to campaigns to see your requests here!",
                },
                invitations: {
                    title: 'No Invitations',
                    message:
                        "You don't have any invitations at the moment. Brands will send you invitations when they're interested in working with you!",
                },
                completed: {
                    title: 'No Completed Jobs',
                    message: "You haven't completed any campaigns yet. Finished campaigns will appear here.",
                },
            };

            const config = emptyStateConfig[activeTab as keyof typeof emptyStateConfig];
            return <EmptyState title={config.title} message={config.message} />;
        }

        return (
            <Stack>
                {activeTab === 'jobs' && (
                    <WorkManagementJobList
                        jobs={mapActiveJobsToListItems(currentData)}
                        onOpenWorkspace={({ campaignId, participantId }) => handleOpenJob(campaignId, participantId)}
                    />
                )}
                {activeTab === 'requests' && (
                    <RequestsList
                        requests={currentData
                            .map(mapRawToBuzzBridgeRequest)
                            .filter((r): r is BuzzBridgeRequestRow => r != null)}
                        onCampaignClick={handleOpenJob}
                    />
                )}
                {activeTab === 'completed' && (
                    <WorkManagementJobList
                        jobs={mapActiveJobsToListItems(currentData)}
                        onOpenWorkspace={({ campaignId, participantId }) => handleOpenJob(campaignId, participantId)}
                    />
                )}
                {activeTab === 'invitations' && (
                    <InvitationsList
                        invitations={currentData}
                        onAccept={handleAcceptInvitation}
                        onReject={handleRejectInvitation}
                    />
                )}
                <Pagination
                    variant="obsidian"
                    currentPage={paginationInfo.currentPage}
                    totalPages={paginationInfo.totalPages}
                    onPageChange={handlePageChange}
                />
            </Stack>
        );
    };

    if (gettingBuzzProfile && !data?.data?.influencer && !noInfluencerProfile) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: theme[themeStyle].primary_orange }} />
            </Box>
        );
    }

    if (!gettingBuzzProfile && noInfluencerProfile && data == null) {
        return (
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <BuzzBridgeCreatorOnboardingScreen onConnectClick={() => setOnboardingOpen(true)} />
                <InfluencerOnboardingModal
                    open={onboardingOpen}
                    onClose={() => setOnboardingOpen(false)}
                    onProfileReady={async () => {
                        setOnboardingOpen(false);
                        await handleGetInitialProfile().catch(() => {});
                    }}
                />
            </Box>
        );
    }

    if (!gettingBuzzProfile && !data?.data?.influencer) {
        return (
            <Box sx={{ minHeight: '100vh', py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                    title="Couldn’t load your profile"
                    message="Check your connection and try again. If the problem continues, contact support."
                    action={
                        <Button
                            variant="contained"
                            onClick={() => {
                                void handleGetInitialProfile().catch(() => {});
                            }}
                            sx={{
                                mt: 1,
                                bgcolor: theme[themeStyle].highlight,
                                color: theme[themeStyle].deep_grey_lighter,
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { bgcolor: theme[themeStyle].highlight, opacity: 0.9 },
                            }}>
                            Retry
                        </Button>
                    }
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                py: { xs: 2, sm: 3, md: 4 },
                pb: { xs: 8, sm: 10, lg: 12 },
                boxSizing: 'border-box',
            }}>
            <Container
                disableGutters
                maxWidth={false}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%' },
                    mx: 'auto',
                    minWidth: 0,
                    px: { xs: 2, sm: 2, md: 2.5 },
                    boxSizing: 'border-box',
                }}>
                <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                    {data?.data?.influencer ? (
                        <CreatorWorkspace
                            influencer={data.data.influencer}
                            invitationCount={
                                data.totalCount?.invitations ??
                                data.data.influencer.invitations_count ??
                                data.data.influencer.invitations?.length ??
                                0
                            }
                            hasPerformance={buildCommissionEnrollmentRows(commissionWallet ?? undefined).length > 0}
                            workspaceView={workspaceView}
                            onWorkspaceView={setWorkspaceView}
                            activeTab={activeTab}
                            tabOptions={tabOptions}
                            onTabChange={handleTabChange}
                            onEditProfile={handleEditProfile}
                            onCashOut={handleCashOutInfo}
                            onOpenCampaign={handleOpenJob}
                            onSaveRateCard={handleSaveRateCard}
                            savingRateCard={gettingBuzzProfile}
                            tabContent={renderTabContent()}
                        />
                    ) : null}
                </Stack>
            </Container>

            {/* Edit Profile Modal */}
            {data?.data?.influencer && (
                <EditProfileModal
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    influencer={data.data.influencer}
                    initialTab={settingsTab}
                    onSave={handleSaveProfile}
                    onPlatformConnected={() => {
                        void handleGetInitialProfile().catch(() => {});
                    }}
                />
            )}

            {/* Cash Out Modal */}
            <CashOutModal open={cashOutOpen} onClose={() => setCashOutOpen(false)} sourceWallet="campaign" />
        </Box>
    );
};
